import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { libraryAPI } from '../api';
import { toast } from 'sonner';
import { ExternalLink, Bookmark, BookmarkCheck } from 'lucide-react';
import type { AttachedArticle } from '../types';

interface AttachedArticleCardProps {
  article: AttachedArticle;
  compact?: boolean;
}

const AttachedArticleCard: React.FC<AttachedArticleCardProps> = ({ article, compact = false }) => {
  const [isInLibrary, setIsInLibrary] = useState(article.is_in_library);
  const [saving, setSaving] = useState(false);

  const handleSaveToLibrary = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    
    if (saving || isInLibrary) return;
    
    setSaving(true);
    try {
      await libraryAPI.saveArticle(article.pmid);
      setIsInLibrary(true);
      toast.success('Article saved to library!');
    } catch (error) {
      console.error('Error saving to library:', error);
      toast.error('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  if (compact) {
    return (
      <Link
        to={`/article/${article.pmid}`}
        className="flex items-center gap-2 p-2 bg-gray-50 rounded border hover:border-blue-200 transition-colors"
        data-testid={`attached-article-card-${article.pmid}`}
      >
        <ExternalLink className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
        <span className="responsive-title text-sm text-gray-900 truncate flex-1">{article.title}</span>
        {!isInLibrary && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSaveToLibrary}
            disabled={saving}
            className="h-6 w-6 p-0 flex-shrink-0"
            title="Save to library"
          >
            <Bookmark className="h-3.5 w-3.5" />
          </Button>
        )}
        {isInLibrary && (
          <BookmarkCheck className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
        )}
      </Link>
    );
  }

  return (
    <div 
      className="p-3 bg-gray-50 rounded-lg border hover:border-blue-200 transition-colors"
      data-testid={`attached-article-card-${article.pmid}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/article/${article.pmid}`}
            className="responsive-title text-sm font-medium text-gray-900 hover:text-blue-600 line-clamp-2 block"
          >
            {article.title}
          </Link>
          <p className="text-xs text-gray-500 mt-1">
            {article.journal} • {article.pub_date}
          </p>
          
          {/* Design Tags */}
          {article.design_tags && article.design_tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {article.design_tags.slice(0, 3).map((tag, idx) => {
                const isHighValue = ['meta-analysis', 'randomized controlled trial', 'systematic review', 'guideline'].some(
                  hvd => tag.toLowerCase().includes(hvd)
                );
                return (
                  <Badge 
                    key={idx} 
                    variant="outline"
                    className={`text-xs py-0 px-1.5 ${
                      isHighValue ? 'bg-green-50 text-green-700 border-green-200' : ''
                    }`}
                  >
                    {tag}
                  </Badge>
                );
              })}
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex items-center gap-2 mt-2">
            <Link to={`/article/${article.pmid}`}>
              <Button variant="ghost" size="sm" className="h-7 text-xs">
                <ExternalLink className="h-3 w-3 mr-1" />
                Open in App
              </Button>
            </Link>
            
            {!isInLibrary ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSaveToLibrary}
                disabled={saving}
                className="h-7 text-xs text-blue-600 hover:text-blue-700"
              >
                <Bookmark className="h-3 w-3 mr-1" />
                {saving ? 'Saving...' : 'Save to Library'}
              </Button>
            ) : (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <BookmarkCheck className="h-3 w-3" />
                In Library
              </span>
            )}
            
            <a
              href={`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-blue-600 ml-auto"
            >
              PMID: {article.pmid}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachedArticleCard;
